import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Alert } from '@/components/ui/alert'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { patchJSON } from '@/lib/api'
import { accountingErrorMessage } from '@/pages/accountingErrors'
import {
  AccountingMappingSelect,
  type AccountingProviderReference,
} from '@/pages/AccountingMappingSelect'

export interface PaymentDepositAccountMappingsResponse {
  payment_deposit_account_mappings: {
    accounts: AccountingProviderReference[]
    accounts_read_only: boolean
    mappings: Array<{
      payment_type: string
      name: string
      account: AccountingProviderReference | null
    }>
  }
}

export function PaymentDepositAccountMappings({
  query,
  readOnly,
}: {
  query: ReturnType<typeof useQuery<PaymentDepositAccountMappingsResponse>>
  readOnly: boolean
}) {
  const queryClient = useQueryClient()
  const update = useMutation({
    mutationFn: ({ paymentType, accountId }: { paymentType: string; accountId: string | null }) =>
      patchJSON<PaymentDepositAccountMappingsResponse>('/api/v1/payment_deposit_account_mappings', {
        payment_deposit_account_mappings: { payment_type: paymentType, account_id: accountId },
      }),
    onSuccess: (data) => queryClient.setQueryData(['payment-deposit-account-mappings'], data),
  })

  if (query.isLoading)
    return <p className="text-body-sm text-muted-foreground">Loading deposit-to accounts...</p>
  if (query.isError || !query.data) return <Alert>Deposit-to accounts unavailable</Alert>

  const payload = query.data.payment_deposit_account_mappings
  return (
    <section className="p-4">
      <h2 className="text-body font-semibold">Payment deposit accounts</h2>
      <div className="pt-3">
        {update.error && <Alert className="mb-3">{accountingErrorMessage(update.error)}</Alert>}
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Offline Payment type</TableHead>
              <TableHead>Deposit-to account</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {payload.mappings.map((mapping) => (
              <TableRow key={mapping.payment_type}>
                <TableCell className="font-medium">{mapping.name}</TableCell>
                <TableCell>
                  {readOnly ? (
                    <span className="text-body-sm text-muted-foreground">
                      {mapping.account?.name ?? 'Unmapped'}
                    </span>
                  ) : (
                    <AccountingMappingSelect
                      destinations={payload.accounts}
                      value={mapping.account?.id}
                      disabled={update.isPending}
                      onChange={(accountId) =>
                        update.mutate({ paymentType: mapping.payment_type, accountId })
                      }
                    />
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </section>
  )
}
